import { useState, useRef } from "react";
import { Reveal } from "./Reveal";
import { GALLERY } from "@/data";
import { MoveHorizontal } from "lucide-react";

const BeforeAfter = ({ item, testid }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const move = (clientX) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, pct)));
  };

  return (
    <div className="clip-frame">
      <div
        ref={ref}
        data-testid={testid}
        className="relative aspect-[4/3] overflow-hidden bg-ink cursor-ew-resize select-none"
        onMouseMove={(e) => dragging.current && move(e.clientX)}
        onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
        onMouseUp={() => (dragging.current = false)}
        onMouseLeave={() => (dragging.current = false)}
        onTouchMove={(e) => move(e.touches[0].clientX)}
      >
        <img src={item.after} alt={`${item.name} after`} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" draggable={false} />
        <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
          <img src={item.before} alt={`${item.name} before`} loading="lazy" decoding="async"
            className="absolute inset-0 h-full object-cover max-w-none" style={{ width: `${100 / (pos / 100)}%` }} draggable={false} />
          <span className="absolute top-4 left-4 bg-ink/80 text-paper font-mono text-[11px] uppercase tracking-widest px-3 py-1">Before</span>
        </div>
        <span className="absolute top-4 right-4 bg-rust text-white font-mono text-[11px] uppercase tracking-widest px-3 py-1">After</span>

        <div className="absolute top-0 bottom-0 w-0.5 bg-paper" style={{ left: `${pos}%` }}>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 bg-paper text-ink grid place-items-center shadow-lg">
            <MoveHorizontal size={18} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const Gallery = () => {
  const [active, setActive] = useState(0);
  const item = GALLERY[active];

  return (
    <section id="gallery" className="bg-paper py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <Reveal>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-rust">Transformations</span>
            <h2 className="mt-5 font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-2xl leading-[0.95]">
              From chaos to order — drag to compare, garage by garage.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2">
              {GALLERY.map((g, i) => (
                <button key={g.name} data-testid={`gallery-tab-${i}`} onClick={() => setActive(i)}
                  className={`px-4 py-2 text-sm font-medium border transition-colors ${
                    active === i ? "bg-ink text-paper border-ink" : "border-ink/20 text-ink/70 hover:border-ink"
                  }`}>
                  {g.name}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal>
          <BeforeAfter item={item} testid="before-after-slider" />
          <div className="mt-5 flex items-center justify-between">
            <p className="font-display font-bold text-xl text-ink">{item.name}</p>
            <p className="font-mono text-xs uppercase tracking-widest text-ink/50">{item.location}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
